/**
 * Sign-in with Google, Apple or Microsoft (ADR 189, plan 51 §2.3). Cognito's
 * hosted endpoints broker the provider: the browser is sent to `/oauth2/authorize`
 * with `identity_provider`, comes back to `/auth/callback` with a code, and the
 * code is exchanged (PKCE, public client, no secret) for the social client's ID
 * token. That token is only a proof: the portal turns it into a clinic session
 * with the custom auth flow (`AuthBackend.federatedSignIn`).
 */

/** Cognito's names for the providers (ADR 189 §2). */
export type SocialProvider = 'Google' | 'SignInWithApple' | 'Microsoft';

export const SOCIAL_PROVIDERS: readonly SocialProvider[] = [
  'Google',
  'SignInWithApple',
  'Microsoft',
];

export const SOCIAL_LABEL: Record<SocialProvider, string> = {
  Google: 'Google',
  SignInWithApple: 'Apple',
  Microsoft: 'Microsoft',
};

/** The public values the portal reads from `config.json`. */
export interface SocialConfig {
  /** `<prefix>.auth.ap-southeast-2.amazoncognito.com`, no scheme. */
  authDomain: string;
  /** The `clinic-portal-social` app client. */
  clientId: string;
  providers: readonly SocialProvider[];
}

/** What the callback hands to the sign-in: the proof and who it is for. */
export interface SocialResult {
  idToken: string;
  provider: SocialProvider;
  email: string;
  name?: string;
  /** Where to go after signing in (kept from before the redirect). */
  returnTo?: string;
}

export const CALLBACK_PATH = '/auth/callback';
const STORE_KEY = 'laura.social';

interface Pending {
  state: string;
  verifier: string;
  provider: SocialProvider;
  redirectUri: string;
  returnTo?: string;
}

/** The providers from `config.json` that the portal knows, in a fixed order; empty when not configured. */
export function socialConfig(value: {
  authDomain?: unknown;
  socialClientId?: unknown;
  socialProviders?: unknown;
}): SocialConfig | undefined {
  const { authDomain, socialClientId, socialProviders } = value;
  if (typeof authDomain !== 'string' || !authDomain) return undefined;
  if (typeof socialClientId !== 'string' || !socialClientId) return undefined;
  if (!Array.isArray(socialProviders)) return undefined;
  const providers = SOCIAL_PROVIDERS.filter((p) => socialProviders.includes(p));
  if (providers.length === 0) return undefined;
  return {
    authDomain: authDomain.replace(/^https?:\/\//, '').replace(/\/+$/, ''),
    clientId: socialClientId,
    providers,
  };
}

function base64Url(bytes: Uint8Array): string {
  let text = '';
  for (const b of bytes) text += String.fromCharCode(b);
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64Url(buf);
}

async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStore(): Store | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** The authorize URL for one provider; remembers the PKCE verifier and state for the callback. */
export async function socialAuthorizeUrl(
  config: SocialConfig,
  provider: SocialProvider,
  options: { origin: string; returnTo?: string; store?: Store },
): Promise<string> {
  const store = options.store ?? defaultStore();
  if (!store)
    throw Object.assign(new Error('Session storage is off'), { name: 'StorageUnavailable' });
  const state = randomString(24);
  const verifier = randomString(48);
  const redirectUri = `${options.origin}${CALLBACK_PATH}`;
  const pending: Pending = {
    state,
    verifier,
    provider,
    redirectUri,
    ...(options.returnTo && { returnTo: options.returnTo }),
  };
  store.setItem(STORE_KEY, JSON.stringify(pending));
  const query = new URLSearchParams({
    identity_provider: provider,
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri,
    state,
    code_challenge: await challengeOf(verifier),
    code_challenge_method: 'S256',
  });
  return `https://${config.authDomain}/oauth2/authorize?${query.toString()}`;
}

/** Sends the browser to the provider. */
export async function startSocial(
  config: SocialConfig,
  provider: SocialProvider,
  returnTo?: string,
): Promise<void> {
  const url = await socialAuthorizeUrl(config, provider, {
    origin: window.location.origin,
    ...(returnTo && { returnTo }),
  });
  window.location.assign(url);
}

export class SocialError extends Error {
  constructor(
    override readonly name:
      'SocialCancelled' | 'SocialStateMismatch' | 'SocialExchangeFailed' | 'SocialNoEmail',
    message: string,
  ) {
    super(message);
  }
}

function decodePayload(jwt: string): Record<string, unknown> {
  const part = jwt.split('.')[1];
  if (!part) throw new SocialError('SocialExchangeFailed', 'Malformed token');
  const text = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(text, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

/** True when this page load is the provider's redirect back to us. */
export function isSocialCallback(location: Pick<Location, 'pathname'>): boolean {
  return location.pathname === CALLBACK_PATH;
}

/**
 * Finishes the redirect: checks `state`, exchanges the code for tokens and returns
 * the ID token with the email in it. The token is checked again by Cognito's
 * custom auth (signature, audience, age); here it is only read.
 */
export async function finishSocial(
  config: SocialConfig,
  href: string,
  deps: { fetcher?: typeof fetch; store?: Store } = {},
): Promise<SocialResult> {
  const store = deps.store ?? defaultStore();
  const fetcher = deps.fetcher ?? fetch;
  const raw = store?.getItem(STORE_KEY);
  store?.removeItem(STORE_KEY);
  const url = new URL(href);
  const error = url.searchParams.get('error');
  if (error) throw new SocialError('SocialCancelled', error);
  const pending = raw ? (JSON.parse(raw) as Pending) : undefined;
  const code = url.searchParams.get('code');
  if (!pending || !code || url.searchParams.get('state') !== pending.state)
    throw new SocialError('SocialStateMismatch', 'The sign-in did not start here');
  const res = await fetcher(`https://${config.authDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: pending.redirectUri,
      code_verifier: pending.verifier,
    }).toString(),
  });
  if (!res.ok) throw new SocialError('SocialExchangeFailed', `HTTP ${res.status}`);
  const body = (await res.json()) as { id_token?: unknown };
  if (typeof body.id_token !== 'string')
    throw new SocialError('SocialExchangeFailed', 'No ID token');
  const claims = decodePayload(body.id_token);
  const email = typeof claims['email'] === 'string' ? claims['email'].trim().toLowerCase() : '';
  if (!email) throw new SocialError('SocialNoEmail', 'The provider shared no email address');
  const name = typeof claims['name'] === 'string' ? claims['name'] : undefined;
  return {
    idToken: body.id_token,
    provider: pending.provider,
    email,
    ...(name && { name }),
    ...(pending.returnTo && { returnTo: pending.returnTo }),
  };
}

/** Plain-language message for a failed provider sign-in. */
export function socialErrorMessage(error: unknown, provider?: SocialProvider): string {
  const name = error instanceof Error ? error.name : '';
  const who = provider ? SOCIAL_LABEL[provider] : 'that account';
  switch (name) {
    case 'SocialCancelled':
      return `Signing in with ${who} was cancelled.`;
    case 'SocialNoEmail':
      return `${who} did not share your email address with Laura. Allow it, or sign in with your email.`;
    case 'NoAccount':
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return `There's no Laura account for that ${who} address yet. Sign in with the email you were invited with, or ask your clinic's Owner to invite this address.`;
    case 'StorageUnavailable':
      return 'Your browser is blocking what this sign-in needs. Sign in with your email and password.';
    default:
      return `Signing in with ${who} did not work. Try again, or sign in with your email.`;
  }
}
